import { useCallback, useEffect, useRef, useState } from "react";

export const CONNECTION_STATES = Object.freeze({
  ONLINE: "online",
  DEGRADED: "degraded",
  OFFLINE: "offline",
  UNREACHABLE: "unreachable",
});

export const CONNECTIVITY_DEFAULTS = Object.freeze({
  probeIntervalMs: 60_000,
  probeJitterMs: 10_000,
  confirmationDelayMs: 5_000,
  probeTimeoutMs: 5_000,
  slowProbeThresholdMs: 1_500,
  failuresBeforeUnreachable: 2,
  slowChecksBeforeDegraded: 2,
  healthyChecksBeforeRecovery: 2,
  incidentBackoffMs: Object.freeze([5_000, 15_000, 30_000, 60_000]),
});

export const NETWORK_TYPES = Object.freeze({
  FAST: {
    type: "4g",
    label: "Connexion rapide",
    color: "#52c41a",
    imageQuality: "high",
    enableAnimations: true,
    enableVideos: true,
    paginationSize: 20,
  },
  MODERATE: {
    type: "3g",
    label: "Connexion normale",
    color: "#295d86",
    imageQuality: "medium",
    enableAnimations: true,
    enableVideos: false,
    paginationSize: 12,
  },
  SLOW: {
    type: "2g",
    label: "Connexion faible ou instable",
    color: "#d48806",
    imageQuality: "low",
    enableAnimations: false,
    enableVideos: false,
    paginationSize: 8,
  },
  OFFLINE: {
    type: "offline",
    label: "Hors connexion",
    color: "#be4349",
    imageQuality: "cached",
    enableAnimations: false,
    enableVideos: false,
    paginationSize: 10,
  },
});

const PROBLEM_STATES = new Set([
  CONNECTION_STATES.DEGRADED,
  CONNECTION_STATES.OFFLINE,
  CONNECTION_STATES.UNREACHABLE,
]);

const getNavigator = () =>
  typeof navigator === "undefined" ? null : navigator;

const getDocument = () => (typeof document === "undefined" ? null : document);

const getMonotonicNow = () =>
  typeof performance !== "undefined" && typeof performance.now === "function"
    ? performance.now()
    : Date.now();

const normalizeMetric = (value) => {
  if (value === null || value === undefined || value === "") {
    return null;
  }

  const parsed = Number(value);
  return Number.isFinite(parsed) && parsed >= 0 ? parsed : null;
};

export function getNetworkInformation(navigatorLike = getNavigator()) {
  const connection =
    navigatorLike?.connection ||
    navigatorLike?.mozConnection ||
    navigatorLike?.webkitConnection ||
    null;

  if (!connection) {
    return {
      supported: false,
      effectiveType: "unknown",
      downlink: null,
      rtt: null,
      saveData: false,
    };
  }

  return {
    supported: true,
    effectiveType:
      typeof connection.effectiveType === "string"
        ? connection.effectiveType.toLowerCase()
        : "unknown",
    downlink: normalizeMetric(connection.downlink),
    rtt: normalizeMetric(connection.rtt),
    // saveData est une préférence utilisateur, pas une preuve de lenteur.
    saveData: connection.saveData === true,
  };
}

export function isSlowNetworkInformation(networkInfo = {}) {
  if (!networkInfo.supported) {
    return false;
  }

  if (
    networkInfo.effectiveType === "2g" ||
    networkInfo.effectiveType === "slow-2g"
  ) {
    return true;
  }

  if (networkInfo.downlink !== null && networkInfo.downlink < 0.75) {
    return true;
  }

  return networkInfo.rtt !== null && networkInfo.rtt >= 1_500;
}

export function determineNetworkType(networkInfo, browserOnline) {
  if (!browserOnline) {
    return NETWORK_TYPES.OFFLINE;
  }

  if (isSlowNetworkInformation(networkInfo)) {
    return NETWORK_TYPES.SLOW;
  }

  if (networkInfo?.effectiveType === "4g") {
    return NETWORK_TYPES.FAST;
  }

  if (
    networkInfo?.downlink !== null &&
    networkInfo?.downlink >= 4 &&
    (networkInfo?.rtt === null || networkInfo.rtt <= 300)
  ) {
    return NETWORK_TYPES.FAST;
  }

  // L'absence de Network Information API ne doit jamais générer une alerte.
  return NETWORK_TYPES.MODERATE;
}

const decorateNetworkStatus = (status) => {
  const networkType = determineNetworkType(
    status.networkInfo,
    status.browserOnline
  );

  return {
    ...status,
    ...status.networkInfo,
    type: networkType.type,
    label: networkType.label,
    color: networkType.color,
    imageQuality: networkType.imageQuality,
    enableAnimations: networkType.enableAnimations,
    enableVideos: networkType.enableVideos,
    paginationSize: networkType.paginationSize,
    isOnline: status.browserOnline,
    isOffline: status.status === CONNECTION_STATES.OFFLINE,
    isUnreachable: status.status === CONNECTION_STATES.UNREACHABLE,
    isDegraded: status.status === CONNECTION_STATES.DEGRADED,
    isSlow: status.status === CONNECTION_STATES.DEGRADED,
    isFast:
      status.status === CONNECTION_STATES.ONLINE &&
      networkType.type === NETWORK_TYPES.FAST.type,
    isModerate:
      status.status === CONNECTION_STATES.ONLINE &&
      networkType.type === NETWORK_TYPES.MODERATE.type,
  };
};

export function createInitialNetworkStatus(navigatorLike = getNavigator()) {
  const browserOnline = navigatorLike?.onLine !== false;
  const networkInfo = getNetworkInformation(navigatorLike);

  return decorateNetworkStatus({
    status: !browserOnline
      ? CONNECTION_STATES.OFFLINE
      : CONNECTION_STATES.ONLINE,
    reason: !browserOnline ? "browser-offline" : null,
    browserOnline,
    networkInfo,
    probeLatencyMs: null,
    lastCheckedAt: null,
    consecutiveFailures: 0,
    consecutiveSlowChecks: 0,
    consecutiveHealthyChecks: 0,
    recoveryPending: false,
    isChecking: false,
  });
}

export function reduceNetworkStatus(
  previous,
  {
    browserOnline,
    networkInfo,
    probeResult,
    checkedAt = Date.now(),
    slowProbeThresholdMs = CONNECTIVITY_DEFAULTS.slowProbeThresholdMs,
    failuresBeforeUnreachable =
      CONNECTIVITY_DEFAULTS.failuresBeforeUnreachable,
    slowChecksBeforeDegraded =
      CONNECTIVITY_DEFAULTS.slowChecksBeforeDegraded,
    healthyChecksBeforeRecovery =
      CONNECTIVITY_DEFAULTS.healthyChecksBeforeRecovery,
    preserveProblemUntilProbe = false,
  }
) {
  const safePrevious = previous || createInitialNetworkStatus();
  const safeNetworkInfo = networkInfo || safePrevious.networkInfo;

  if (!browserOnline) {
    return decorateNetworkStatus({
      ...safePrevious,
      status: CONNECTION_STATES.OFFLINE,
      reason: "browser-offline",
      browserOnline: false,
      networkInfo: safeNetworkInfo,
      consecutiveFailures: 0,
      consecutiveSlowChecks: 0,
      consecutiveHealthyChecks: 0,
      recoveryPending: false,
      isChecking: false,
    });
  }

  const nativeSlow = isSlowNetworkInformation(safeNetworkInfo);

  if (!probeResult) {
    const mustKeepProblem =
      preserveProblemUntilProbe && PROBLEM_STATES.has(safePrevious.status);
    return decorateNetworkStatus({
      ...safePrevious,
      status: mustKeepProblem
        ? safePrevious.status
        : CONNECTION_STATES.ONLINE,
      reason: mustKeepProblem ? safePrevious.reason : null,
      browserOnline: true,
      networkInfo: safeNetworkInfo,
      recoveryPending: mustKeepProblem,
    });
  }

  if (!probeResult.reachable) {
    const consecutiveFailures = safePrevious.consecutiveFailures + 1;
    const confirmedFailure =
      consecutiveFailures >= failuresBeforeUnreachable;
    const awaitingOnlineConfirmation =
      !confirmedFailure && safePrevious.status === CONNECTION_STATES.OFFLINE;

    return decorateNetworkStatus({
      ...safePrevious,
      status: confirmedFailure
        ? CONNECTION_STATES.UNREACHABLE
        : safePrevious.status,
      reason: confirmedFailure
        ? probeResult.serverResponded
          ? "service-unhealthy"
          : "health-unreachable"
        : safePrevious.reason,
      browserOnline: true,
      networkInfo: safeNetworkInfo,
      probeLatencyMs: probeResult.latencyMs,
      lastCheckedAt: checkedAt,
      consecutiveFailures,
      consecutiveSlowChecks: 0,
      consecutiveHealthyChecks: 0,
      recoveryPending: awaitingOnlineConfirmation,
      isChecking: false,
    });
  }

  const slowProbe =
    Number.isFinite(probeResult.latencyMs) &&
    probeResult.latencyMs >= slowProbeThresholdMs;
  // Une sonde HEAD mesure surtout la latence et ne permet pas d'estimer un
  // faible débit. Les métriques Network Information persistantes comptent donc
  // aussi comme une observation lente, avec la même confirmation sur 2 mesures.
  const slowObservation = slowProbe || nativeSlow;
  const consecutiveSlowChecks = slowObservation
    ? safePrevious.consecutiveSlowChecks + 1
    : 0;
  const consecutiveHealthyChecks = slowObservation
    ? 0
    : safePrevious.consecutiveHealthyChecks + 1;
  const confirmedSlow =
    consecutiveSlowChecks >= slowChecksBeforeDegraded;
  const wasProblem = PROBLEM_STATES.has(safePrevious.status);
  const recovered =
    !slowObservation &&
    (!wasProblem ||
      consecutiveHealthyChecks >= healthyChecksBeforeRecovery);
  const nextStatus =
    confirmedSlow
      ? CONNECTION_STATES.DEGRADED
      : recovered
        ? CONNECTION_STATES.ONLINE
        : safePrevious.status;

  return decorateNetworkStatus({
    ...safePrevious,
    status: nextStatus,
    reason: confirmedSlow
      ? nativeSlow
        ? "network-information"
        : "health-latency"
        : recovered
          ? null
          : safePrevious.reason,
    browserOnline: true,
    networkInfo: safeNetworkInfo,
    probeLatencyMs: probeResult.latencyMs,
    lastCheckedAt: checkedAt,
    consecutiveFailures: 0,
    consecutiveSlowChecks,
    consecutiveHealthyChecks,
    recoveryPending: wasProblem && !recovered,
    isChecking: false,
  });
}

export function getJitteredProbeDelay(
  baseMs = CONNECTIVITY_DEFAULTS.probeIntervalMs,
  jitterMs = CONNECTIVITY_DEFAULTS.probeJitterMs,
  randomValue = Math.random()
) {
  const safeBase = Math.max(1_000, Number(baseMs) || 0);
  const safeJitter = Math.max(0, Number(jitterMs) || 0);
  const boundedRandom = Math.min(1, Math.max(0, Number(randomValue) || 0));
  return Math.max(
    1_000,
    Math.round(safeBase + (boundedRandom * 2 - 1) * safeJitter)
  );
}

export function getIncidentProbeDelay(
  attempt,
  randomValue = Math.random(),
  backoffMs = CONNECTIVITY_DEFAULTS.incidentBackoffMs
) {
  const safeAttempt = Math.max(0, Math.trunc(Number(attempt) || 0));
  const index = Math.min(safeAttempt, backoffMs.length - 1);
  const baseMs = backoffMs[index];
  const jitterMs = Math.min(5_000, Math.round(baseMs * 0.15));
  return getJitteredProbeDelay(baseMs, jitterMs, randomValue);
}

export async function probeElsaConnectivity({
  fetchImpl = typeof fetch === "function" ? fetch : null,
  timeoutMs = CONNECTIVITY_DEFAULTS.probeTimeoutMs,
  endpoint = "/health",
  signal,
  now = getMonotonicNow,
} = {}) {
  if (typeof fetchImpl !== "function") {
    return {
      reachable: false,
      serverResponded: false,
      latencyMs: null,
      reason: "fetch-unavailable",
    };
  }

  const controller = new AbortController();
  let timedOut = false;
  const abortFromCaller = () => controller.abort();

  if (signal) {
    if (signal.aborted) {
      controller.abort();
    } else {
      signal.addEventListener("abort", abortFromCaller, { once: true });
    }
  }

  const timeoutId = setTimeout(() => {
    timedOut = true;
    controller.abort();
  }, Math.max(1, Number(timeoutMs) || CONNECTIVITY_DEFAULTS.probeTimeoutMs));
  const startedAt = now();
  const separator = endpoint.includes("?") ? "&" : "?";
  const probeUrl = `${endpoint}${separator}connectivityCheck=${Date.now()}`;

  try {
    const response = await fetchImpl(probeUrl, {
      method: "HEAD",
      cache: "no-store",
      credentials: "same-origin",
      signal: controller.signal,
    });
    const latencyMs = Math.max(0, Math.round(now() - startedAt));

    return {
      reachable: Boolean(response?.ok),
      serverResponded: true,
      statusCode: response?.status || null,
      latencyMs,
      reason: response?.ok ? null : "health-status",
    };
  } catch (error) {
    return {
      reachable: false,
      serverResponded: false,
      latencyMs: Math.max(0, Math.round(now() - startedAt)),
      timedOut,
      aborted: Boolean(signal?.aborted),
      reason: timedOut ? "timeout" : "network-error",
    };
  } finally {
    clearTimeout(timeoutId);
    signal?.removeEventListener?.("abort", abortFromCaller);
  }
}

export function useNetworkStatus({
  enableProbe = false,
  probeIntervalMs = CONNECTIVITY_DEFAULTS.probeIntervalMs,
  probeJitterMs = CONNECTIVITY_DEFAULTS.probeJitterMs,
  confirmationDelayMs = CONNECTIVITY_DEFAULTS.confirmationDelayMs,
  probeTimeoutMs = CONNECTIVITY_DEFAULTS.probeTimeoutMs,
  slowProbeThresholdMs = CONNECTIVITY_DEFAULTS.slowProbeThresholdMs,
  endpoint = "/health",
  fetchImpl = undefined,
} = {}) {
  const [networkStatus, setNetworkStatus] = useState(() =>
    createInitialNetworkStatus()
  );
  const statusRef = useRef(networkStatus);
  const mountedRef = useRef(true);
  const inFlightRef = useRef(null);
  const probeAbortRef = useRef(null);
  const manualCheckRef = useRef(null);

  const commitStatus = useCallback((updater) => {
    if (!mountedRef.current) {
      return;
    }

    setNetworkStatus((previous) => {
      const next =
        typeof updater === "function" ? updater(previous) : updater;
      statusRef.current = next;
      return next;
    });
  }, []);

  const readBrowserState = useCallback(() => {
    const navigatorLike = getNavigator();
    return {
      browserOnline: navigatorLike?.onLine !== false,
      networkInfo: getNetworkInformation(navigatorLike),
    };
  }, []);

  const updateFromBrowser = useCallback(() => {
    const browserState = readBrowserState();
    commitStatus((previous) =>
      reduceNetworkStatus(previous, {
        ...browserState,
        preserveProblemUntilProbe: enableProbe,
      })
    );
    return browserState;
  }, [commitStatus, enableProbe, readBrowserState]);

  const checkNow = useCallback(() => {
    if (inFlightRef.current) {
      return inFlightRef.current;
    }

    const browserState = updateFromBrowser();
    if (!browserState.browserOnline) {
      return Promise.resolve({
        reachable: false,
        browserOffline: true,
        latencyMs: null,
      });
    }

    const controller = new AbortController();
    probeAbortRef.current = controller;
    commitStatus((previous) => ({ ...previous, isChecking: true }));

    const probePromise = probeElsaConnectivity({
      fetchImpl,
      timeoutMs: probeTimeoutMs,
      endpoint,
      signal: controller.signal,
    })
      .then((probeResult) => {
        if (!mountedRef.current) {
          return probeResult;
        }

        if (probeResult.aborted) {
          commitStatus((previous) => ({ ...previous, isChecking: false }));
          return probeResult;
        }

        const latestBrowserState = readBrowserState();
        commitStatus((previous) =>
          reduceNetworkStatus(previous, {
            ...latestBrowserState,
            probeResult,
            slowProbeThresholdMs,
          })
        );
        return probeResult;
      })
      .finally(() => {
        if (inFlightRef.current === probePromise) {
          inFlightRef.current = null;
        }
        if (probeAbortRef.current === controller) {
          probeAbortRef.current = null;
        }
      });

    inFlightRef.current = probePromise;
    return probePromise;
  }, [
    commitStatus,
    endpoint,
    fetchImpl,
    probeTimeoutMs,
    readBrowserState,
    slowProbeThresholdMs,
    updateFromBrowser,
  ]);

  useEffect(() => {
    mountedRef.current = true;
    const navigatorLike = getNavigator();
    const documentLike = getDocument();
    const connection =
      navigatorLike?.connection ||
      navigatorLike?.mozConnection ||
      navigatorLike?.webkitConnection ||
      null;
    let timerId = null;
    let disposed = false;
    let incidentAttempt = 0;
    let schedulerPromise = null;

    const clearScheduledCheck = () => {
      if (timerId !== null) {
        clearTimeout(timerId);
        timerId = null;
      }
    };

    const scheduleCheck = (delayMs) => {
      if (!enableProbe || disposed || documentLike?.hidden) {
        return;
      }

      clearScheduledCheck();
      timerId = setTimeout(() => void runAndSchedule(), delayMs);
    };

    const runAndSchedule = () => {
      if (schedulerPromise) {
        return schedulerPromise;
      }

      schedulerPromise = (async () => {
        clearScheduledCheck();
        if (disposed || documentLike?.hidden) {
          return null;
        }

        const result = await checkNow();
        if (disposed || documentLike?.hidden || result?.browserOffline) {
          return result;
        }

        if (result?.aborted) {
          // Lors d'un redémarrage d'effet (notamment React.StrictMode), le
          // nouveau scheduler peut avoir rejoint la sonde que l'ancien cleanup
          // vient d'annuler. Relancer après sa libération évite d'arrêter la
          // détection automatique.
          if (getNavigator()?.onLine !== false) {
            scheduleCheck(0);
          }
          return result;
        }

        const current = statusRef.current;
        const probeNeedsIncidentRetry =
          !result?.reachable ||
          result?.latencyMs >= slowProbeThresholdMs ||
          current.consecutiveSlowChecks > 0;
        if (probeNeedsIncidentRetry) {
          const incidentDelay = getIncidentProbeDelay(incidentAttempt);
          incidentAttempt += 1;
          scheduleCheck(
            incidentAttempt === 1
              ? Math.min(confirmationDelayMs, incidentDelay)
              : incidentDelay
          );
          return result;
        }

        incidentAttempt = 0;
        if (PROBLEM_STATES.has(current.status)) {
          scheduleCheck(confirmationDelayMs);
          return result;
        }

        scheduleCheck(getJitteredProbeDelay(probeIntervalMs, probeJitterMs));
        return result;
      })().finally(() => {
        schedulerPromise = null;
      });

      return schedulerPromise;
    };

    const requestImmediateCheck = () => {
      clearScheduledCheck();
      const pendingRun = schedulerPromise;
      if (pendingRun) {
        return pendingRun.finally(() => {
          if (!disposed && !documentLike?.hidden) {
            return runAndSchedule();
          }
          return null;
        });
      }

      return runAndSchedule();
    };

    const handleOffline = () => {
      clearScheduledCheck();
      incidentAttempt = 0;
      probeAbortRef.current?.abort();
      updateFromBrowser();
    };

    const handleOnline = () => {
      incidentAttempt = 0;
      updateFromBrowser();
      if (enableProbe && !documentLike?.hidden) {
        void requestImmediateCheck();
      }
    };

    const handleConnectionChange = () => {
      updateFromBrowser();
      if (
        enableProbe &&
        !documentLike?.hidden &&
        !PROBLEM_STATES.has(statusRef.current.status)
      ) {
        // Les événements Network Information peuvent osciller rapidement.
        // On les regroupe sans casser le backoff d'un incident déjà confirmé.
        scheduleCheck(confirmationDelayMs);
      }
    };

    const handleVisibilityChange = () => {
      if (documentLike?.hidden) {
        clearScheduledCheck();
        probeAbortRef.current?.abort();
        return;
      }

      handleOnline();
    };

    window.addEventListener("online", handleOnline);
    window.addEventListener("offline", handleOffline);
    documentLike?.addEventListener("visibilitychange", handleVisibilityChange);
    connection?.addEventListener?.("change", handleConnectionChange);

    updateFromBrowser();
    manualCheckRef.current = requestImmediateCheck;
    if (enableProbe && !documentLike?.hidden) {
      void runAndSchedule();
    }

    return () => {
      disposed = true;
      mountedRef.current = false;
      if (manualCheckRef.current === requestImmediateCheck) {
        manualCheckRef.current = null;
      }
      clearScheduledCheck();
      probeAbortRef.current?.abort();
      window.removeEventListener("online", handleOnline);
      window.removeEventListener("offline", handleOffline);
      documentLike?.removeEventListener(
        "visibilitychange",
        handleVisibilityChange
      );
      connection?.removeEventListener?.(
        "change",
        handleConnectionChange
      );
    };
  }, [
    checkNow,
    confirmationDelayMs,
    enableProbe,
    probeIntervalMs,
    probeJitterMs,
    slowProbeThresholdMs,
    updateFromBrowser,
  ]);

  const checkNowAndSchedule = useCallback(
    () =>
      typeof manualCheckRef.current === "function"
        ? manualCheckRef.current()
        : checkNow(),
    [checkNow]
  );

  return {
    ...networkStatus,
    checkNow: checkNowAndSchedule,
  };
}

export function getNetworkMessage(networkStatus) {
  if (networkStatus?.status === CONNECTION_STATES.OFFLINE) {
    if (networkStatus.recoveryPending) {
      return {
        level: "warning",
        title: "Connexion détectée, vérification en cours",
        description:
          "ELSA GESTION confirme que le service est de nouveau joignable.",
      };
    }

    return {
      level: "error",
      title: "Aucune connexion Internet détectée",
      description:
        "Vérifiez le Wi-Fi ou les données mobiles, puis relancez le test avant de contacter l’assistance technique.",
    };
  }

  if (networkStatus?.status === CONNECTION_STATES.UNREACHABLE) {
    return {
      level: "error",
      title: "ELSA GESTION est momentanément inaccessible",
      description:
        "Votre appareil indique une connexion, mais le service ne répond pas. Vérifiez si un autre site s’ouvre, puis réessayez.",
    };
  }

  if (networkStatus?.status === CONNECTION_STATES.DEGRADED) {
    if (networkStatus.reason === "health-latency") {
      return {
        level: "warning",
        title: "Connexion ou accès à ELSA ralenti",
        description:
          "Le test d’accès à ELSA GESTION est lent. Vérifiez votre connexion ; si les autres sites fonctionnent normalement, le service peut être momentanément chargé.",
      };
    }

    return {
      level: "warning",
      title: "Connexion faible ou instable",
      description:
        "Les chargements peuvent prendre plus de temps. Vérifiez le Wi-Fi ou les données mobiles avant de contacter l’assistance technique.",
    };
  }

  return null;
}

export function getImageQualityForNetwork(networkType) {
  const typeKey = String(networkType || "3g").toLowerCase();
  const type =
    {
      "4g": NETWORK_TYPES.FAST,
      "3g": NETWORK_TYPES.MODERATE,
      "2g": NETWORK_TYPES.SLOW,
      "slow-2g": NETWORK_TYPES.SLOW,
      offline: NETWORK_TYPES.OFFLINE,
    }[typeKey] || NETWORK_TYPES.MODERATE;

  const cacheOnly = type.imageQuality === "cached";

  return {
    // LazyImage accepte low|medium|high. En mode hors connexion, conserver le
    // profil medium historique et signaler séparément que le cache est requis.
    quality: cacheOnly ? "medium" : type.imageQuality,
    cacheOnly,
    maxWidth:
      type.imageQuality === "high"
        ? 1200
        : type.imageQuality === "medium"
          ? 800
          : 480,
    format:
      type.imageQuality === "high" || type.imageQuality === "medium"
        ? "webp"
        : "jpeg",
    lazyLoad: true,
    placeholder: type.imageQuality !== "high",
  };
}

export function shouldEnableFeature(feature, networkStatus) {
  if (networkStatus?.isOffline || networkStatus?.isUnreachable) {
    return false;
  }

  switch (feature) {
    case "animations":
      return networkStatus?.enableAnimations !== false;
    case "videos":
      return networkStatus?.enableVideos === true;
    case "autoplay":
    case "preload":
      return networkStatus?.isFast === true;
    case "background-sync":
      return true;
    default:
      return true;
  }
}

export function getAdaptiveTimeout(networkStatus, requestType = "api") {
  if (networkStatus?.isOffline) {
    return 3_000;
  }

  const baseTimeouts = {
    api: { fast: 5_000, moderate: 10_000, slow: 15_000 },
    image: { fast: 5_000, moderate: 10_000, slow: 15_000 },
    video: { fast: 10_000, moderate: 20_000, slow: 30_000 },
  };
  const timeouts = baseTimeouts[requestType] || baseTimeouts.api;

  if (networkStatus?.isFast) return timeouts.fast;
  if (networkStatus?.isSlow || networkStatus?.isUnreachable) {
    return timeouts.slow;
  }
  return timeouts.moderate;
}

const networkDetector = {
  CONNECTION_STATES,
  CONNECTIVITY_DEFAULTS,
  NETWORK_TYPES,
  createInitialNetworkStatus,
  determineNetworkType,
  getAdaptiveTimeout,
  getImageQualityForNetwork,
  getIncidentProbeDelay,
  getNetworkInformation,
  getNetworkMessage,
  isSlowNetworkInformation,
  probeElsaConnectivity,
  reduceNetworkStatus,
  shouldEnableFeature,
  useNetworkStatus,
};

export default networkDetector;
